import React, { useState, useCallback } from 'react';
import SportSelector from './SportSelector';
import TextArticleGallery from './TextArticleGallery';
import ArticlePreview from './ArticlePreview';
// LLM hook commented out for v1.1 - keep for future use
// import useArticleGenerator from '../hooks/useArticleGenerator';
import {
  fetchNewsByType,
  uploadArticle,
  markTextAsUsed,
} from '../services/newsService';
import { uploadImageFile } from '../services/blobService';
import { CheckCircle, RefreshCw, ArrowLeft } from 'lucide-react';

/**
 * Main Article Creator Component
 * Orchestrates the article creation workflow
 *
 * v1.1 Flow: Select text article -> Add image -> Upload
 */
function ArticleCreator() {
  // State for sport selection and content
  const [selectedSport, setSelectedSport] = useState(null);
  const [textArticles, setTextArticles] = useState([]);
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const [contentError, setContentError] = useState(null);

  // State for selected article and image
  const [selectedTextArticle, setSelectedTextArticle] = useState(null);
  const [droppedImage, setDroppedImage] = useState(null); // { file: File, preview: string }

  // State for submission
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [uploadComplete, setUploadComplete] = useState(false);

  // LLM hook commented out for v1.1 - keep for future use
  // const {
  //   generatedArticle,
  //   matchedTextArticle,
  //   isGenerating,
  //   error: generationError,
  //   generateArticle,
  //   regenerateArticle,
  //   clearArticle,
  // } = useArticleGenerator();

  /**
   * Fetch content when sport is selected
   */
  const loadContent = useCallback(async (sport) => {
    setIsLoadingContent(true);
    setContentError(null);

    try {
      const { textArticles } = await fetchNewsByType(sport.code);
      setTextArticles(textArticles);
    } catch (err) {
      setContentError(err.message || 'Failed to load content');
      setTextArticles([]);
    } finally {
      setIsLoadingContent(false);
    }
  }, []);

  /**
   * Handle sport selection
   */
  const handleSelectSport = (sport) => {
    setSelectedSport(sport);
    setSelectedTextArticle(null);
    setDroppedImage(null);
    setUploadComplete(false);
    setSubmitStatus(null);
    loadContent(sport);
  };

  /**
   * Handle text article selection
   */
  const handleSelectTextArticle = (article) => {
    setSelectedTextArticle(article);
    setDroppedImage(null);
    setUploadComplete(false);
    setSubmitStatus(null);
  };

  /**
   * Handle image drop/upload
   */
  const handleImageDrop = (file) => {
    const preview = URL.createObjectURL(file);
    setDroppedImage({ file, preview });
  };

  /**
   * Handle removing dropped image
   */
  const handleRemoveImage = () => {
    if (droppedImage?.preview) {
      URL.revokeObjectURL(droppedImage.preview);
    }
    setDroppedImage(null);
  };

  /**
   * Handle going back to article list
   */
  const handleBack = () => {
    handleRemoveImage();
    setSelectedTextArticle(null);
    setSubmitStatus(null);
  };

  /**
   * Handle article approval - upload image, upload article, mark as used
   */
  const handleApprove = async () => {
    if (!selectedTextArticle || !droppedImage || !selectedSport) return;

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Use the article's GUID from the API
      const guid = selectedTextArticle.GUID;

      if (!guid) {
        throw new Error('Article is missing GUID');
      }

      // Step 1: Upload image to CDN (both -medium.jpg and -small.jpg)
      setSubmitStatus('Uploading image to CDN...');
      const uploadResult = await uploadImageFile(droppedImage.file, guid);
      console.log('✅ Images uploaded:', uploadResult);

      // Step 2: Upload the article
      setSubmitStatus('Publishing article...');
      const description = selectedTextArticle.BodyText.length > 150
        ? selectedTextArticle.BodyText.substring(0, 147) + '...'
        : selectedTextArticle.BodyText;

      await uploadArticle({
        sportId: selectedSport.id,
        title: selectedTextArticle.HeadLine,
        description: description,
        type: 'composite',
        guid: guid,
        articleBody: selectedTextArticle.BodyText,
      });

      // Step 3: Mark text article as used
      setSubmitStatus('Finalizing...');
      await markTextAsUsed(selectedTextArticle.TextMetadataID);

      // Remove article from local state
      setTextArticles((prev) =>
        prev.filter((t) => t.TextMetadataID !== selectedTextArticle.TextMetadataID)
      );

      // Show success
      setUploadComplete(true);
      setSubmitStatus(null);
    } catch (err) {
      setSubmitStatus(null);
      alert(`Failed to publish article: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handle continue with same sport
   */
  const handleContinueSameSport = () => {
    handleRemoveImage();
    setSelectedTextArticle(null);
    setUploadComplete(false);
    setSubmitStatus(null);
    loadContent(selectedSport);
  };

  /**
   * Handle change sport
   */
  const handleChangeSport = () => {
    handleRemoveImage();
    setSelectedSport(null);
    setSelectedTextArticle(null);
    setTextArticles([]);
    setUploadComplete(false);
    setSubmitStatus(null);
  };

  // Success screen after upload
  if (uploadComplete) {
    return (
      <div>
        <div className="card card-custom p-5 text-center">
          <CheckCircle size={64} className="text-success mx-auto mb-4" />
          <h3 className="text-white mb-3">Article Published Successfully!</h3>
          <p className="text-muted mb-4">
            The article "{selectedTextArticle?.HeadLine}" has been published with the attached image.
          </p>
          <div className="d-flex gap-3 justify-content-center">
            <button
              onClick={handleContinueSameSport}
              className="btn btn-primary-custom d-flex align-items-center"
            >
              <RefreshCw size={18} className="me-2" />
              Create Another ({selectedSport?.name})
            </button>
            <button
              onClick={handleChangeSport}
              className="btn btn-secondary-custom d-flex align-items-center"
            >
              <ArrowLeft size={18} className="me-2" />
              Change Sport
            </button>
          </div>
        </div>

        {/* Stats Summary */}
        <div className="card card-custom p-3 mt-4">
          <div className="text-center text-white">
            <h5 className="mb-0">{textArticles.length}</h5>
            <small className="text-muted">Remaining Articles for {selectedSport?.name}</small>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Status Message */}
      {submitStatus && (
        <div className="alert alert-info mb-4" role="alert">
          <div className="d-flex align-items-center">
            <div className="spinner-border spinner-border-sm me-2" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            {submitStatus}
          </div>
        </div>
      )}

      {/* Sport Selector */}
      <SportSelector
        selectedSport={selectedSport}
        onSelectSport={handleSelectSport}
        disabled={isLoadingContent || isSubmitting}
      />

      {/* Content Error */}
      {contentError && (
        <div className="alert alert-danger mb-4" role="alert">
          {contentError}
        </div>
      )}

      {/* Text Article Gallery */}
      {selectedSport && !selectedTextArticle && (
        <TextArticleGallery
          textArticles={textArticles}
          onSelectArticle={handleSelectTextArticle}
          isLoading={isLoadingContent}
        />
      )}

      {/* Article Preview with Image Drop Zone */}
      {selectedTextArticle && (
        <ArticlePreview
          textArticle={selectedTextArticle}
          droppedImage={droppedImage}
          isSubmitting={isSubmitting}
          onImageDrop={handleImageDrop}
          onRemoveImage={handleRemoveImage}
          onApprove={handleApprove}
          onBack={handleBack}
        />
      )}

      {/* Stats Summary */}
      {selectedSport && !selectedTextArticle && !isLoadingContent && (
        <div className="card card-custom p-3 mt-4">
          <div className="text-center text-white">
            <h5 className="mb-0">{textArticles.length}</h5>
            <small className="text-muted">Available Articles for {selectedSport.name}</small>
          </div>
        </div>
      )}
    </div>
  );
}

export default ArticleCreator;
